import {
    ColumnFiltersState,
    ColumnOrderState,
    SortingState,
    TableState,
    VisibilityState
} from '@tanstack/react-table';
import { debug as logger } from 'application/Shared/Utils';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

/**
 * A custom hook that extracts and parses table state from URL parameters.
 *
 * @param tableName - The name of the table, used as a prefix for URL parameters.
 * @returns An object containing the parsed table state from URL parameters.
 */
export const useTableStateFromURL = (
    tableName: string
): Partial<TableState> => {
    // Initialize state to store the parsed table state
    const [tableStateFromURL, setTableStateFromURL] = useState<
        Partial<TableState>
    >({});

    const [searchParams] = useSearchParams();

    /**
     * Parses a JSON string from URL parameters.
     *
     * @param key - The key to look up in URL parameters.
     * @returns The parsed value or undefined if not found or invalid.
     */
    function parseParam<T>(property: string): T | undefined {
        const stateProperty = searchParams.get(`${tableName}.${property}`);
        if (stateProperty === null) {
            return undefined;
        }

        try {
            return JSON.parse(stateProperty) as T;
        } catch (error) {
            logger.warn(
                `Invalid JSON for ${property} in URL parameters:`,
                error
            );
            return undefined;
        }
    }

    useEffect(() => {
        // Extract and parse each table state property from URL parameters
        const columnFilters = parseParam<ColumnFiltersState>('columnFilters');
        const columnOrder = parseParam<ColumnOrderState>('columnOrder');
        const columnVisibility =
            parseParam<VisibilityState>('columnVisibility');
        const sorting = parseParam<SortingState>('sorting');

        // Update the state with the parsed values, we set only the values that are not undefined
        setTableStateFromURL({
            ...(columnFilters && { columnFilters }),
            ...(columnOrder && { columnOrder }),
            ...(columnVisibility && { columnVisibility }),
            ...(sorting && { sorting })
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps -- only run on mount
    }, []);

    return tableStateFromURL;
};
